import React, { Fragment } from "react";
import html2canvas from "html2canvas";
import { IoMdClose } from "react-icons/io";
import { BiDownload } from "react-icons/bi";
import { getStatusColor } from "./statusTag";
import { useSelector } from "react-redux";
import { Modal, Typography, Tag } from "components";
import pocketMoniLogo from "assets/images/pocketmoni-logo.png";
import { setCurrentTransaction } from "redux/actions/transactionActions";
import { formatAmount, formatDateTimeExact, isAdminUser } from "utils/helper";

const TransactionDetailsModal = () => {
  const { currentTransaction } = useSelector(
    (state) => state.transactionsReducer
  );

  const { user } = useSelector((state) => state.user);

  const {
    paymentType,
    terminalId,
    serial,
    reference,
    rrn,
    stan,
    maskedPan,
    amountSettled,
    responseMessage,
    responseCode,
    createdDate,
    status,
    transMode,
    cardHolder,
    merchantLocation,
    batteryLevel,
    processor,
    agentId,
    merchantId,
    destBank,
    transAmount,
    // customerContact,
    // narrationType,
  } = currentTransaction || {};

  const { textColor, bgColor, text } = getStatusColor(status);

  const printRef = React.useRef();

  const handleDownloadImage = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = `PocketMoni-${currentTransaction.rrn}.jpg`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  const agentLabels = [
    {
      label: "Transaction Type",
      value: paymentType || "-",
    },
    {
      label: "Terminal ID",
      value: terminalId || "-",
    },
    {
      label: "Serial No",
      value: serial || "-",
    },
    {
      label: "Beneficiary Bank",
      value: destBank || "-",
    },
    {
      label: "RRN",
      value: rrn || "-",
    },
    // {
    //   label: "Cashpoint RRN",
    //   value: rrn || "-",
    // },
    {
      label: "Transaction STAN",
      value: stan || "-",
    },
    {
      label: "Masked PAN",
      value: maskedPan || "-",
    },
    {
      label: "Amount Settled",
      value: amountSettled ? "₦" + formatAmount(amountSettled) : "-",
    },
    {
      label: "Transaction Amount",
      value: transAmount ? "₦" + formatAmount(transAmount) : "-",
    },
    {
      label: "Response Message",
      value: responseMessage || "-",
    },
    {
      label: "Response Code",
      value: responseCode || "-",
    },
    {
      label: "Transaction Date",
      value: createdDate ? formatDateTimeExact(createdDate) : "-",
    },
    {
      label: "Transaction Status",
      value: (
        <Tag text={text} backgroundColor={bgColor} textColor={textColor} />
      ),
    },
  ];

  const adminLabels = [
    {
      label: "Transaction Type",
      value: paymentType || "-",
    },
    {
      label: "Transaction Mode",
      value: transMode || "-",
    },

    {
      label: "Agent ID",
      value: agentId || "-",
    },
    {
      label: "Terminal ID",
      value: terminalId || "-",
    },
    {
      label: "Serial No",
      value: serial || "-",
    },
    {
      label: "Card Holder Name",
      value: cardHolder || "-",
    },
    {
      label: "Beneficiary Bank",
      value: destBank || "-",
    },
    {
      label: "Merchant ID",
      value: merchantId || "-",
    },
    {
      label: "Merchant Location",
      value: merchantLocation || "-",
    },
    {
      label: "Cashpoint Reference",
      value: reference || "-",
    },
    {
      label: "Cashpoint RRN",
      value: rrn || "-",
    },
    {
      label: "Processor Name",
      value: processor || "-",
    },
    {
      label: "Transaction STAN",
      value: stan || "-",
    },
    {
      label: "Terminal Battery Level",
      value: batteryLevel ? `${batteryLevel}%` : "-",
    },
    {
      label: "Masked PAN",
      value: maskedPan || "-",
    },
    {
      label: "Transaction Amount",
      value: transAmount ? "₦" + formatAmount(transAmount) : "-",
    },
    {
      label: "Amount Settled",
      value: amountSettled ? "₦" + formatAmount(amountSettled) : "-",
    },
    {
      label: "Response Message",
      value: responseMessage || "-",
    },
    {
      label: "Response Code",
      value: responseCode || "-",
    },
    // {
    //   label: "Customer Contact",
    //   value: customerContact || "-",
    // },
    // {
    //   label: "Narration Type",
    //   value: narrationType || "-",
    // },
    {
      label: "Transaction Date",
      value: createdDate ? formatDateTimeExact(createdDate) : "-",
    },
    {
      label: "Transaction Status",
      value: (
        <Tag text={text} backgroundColor={bgColor} textColor={textColor} />
      ),
    },
  ];

  const labelsToUse = isAdminUser(user) ? adminLabels : agentLabels;

  return (
    <div>
      <Modal
        showModal={!!currentTransaction}
        customPadding={"0"}
        onClose={() => setCurrentTransaction(null)}
      >
        <div ref={printRef}>
          {" "}
          <div className="transaction-detail-modal">
            <div className="header">
              <div className="d-flex justify-content-end ">
                <IoMdClose
                  className="cursor-pointer"
                  onClick={() => setCurrentTransaction(null)}
                />
              </div>
              <Typography className={"text-center"}>
                Transaction Details
              </Typography>
            </div>

            <Typography className={"text-center sub-header"}>
              Displayed below are the full details of this transaction
            </Typography>
          </div>
          <div className="detail-body mt-4 px-4 pb-2">
            <div className=" row">
              {labelsToUse.map((info, i) => (
                <Fragment key={i}>
                  <div className="col-6 d-flex justify-content-end align-items-center mb-2">
                    <Typography variant="caption2">{info.label}:</Typography>
                  </div>

                  <div className="col-6 d-flex justify-content-start align-items-center mb-2">
                    {" "}
                    <Typography
                      variant="caption1"
                      style={{ wordBreak: "break-all" }}
                    >
                      {info.value}
                    </Typography>
                  </div>
                </Fragment>
              ))}
            </div>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <img src={pocketMoniLogo} width="70" alt="PocketMoni" />
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-center mt-2 px-4 pb-3">
          <BiDownload
            fontSize="12px"
            className="cursor-pointer"
            onClick={handleDownloadImage}
          />{" "}
        </div>
      </Modal>
    </div>
  );
};

export default TransactionDetailsModal;
